import { NextApiRequest } from "next";
import { ReadonlyHeaders } from "next/dist/server/web/spec-extension/adapters/headers";
import { env } from "~/env.mjs";

export const dedupe = <Type>(arr: Type[], key: string): Type[] => {
    const uniqueIds = new Set();

    return arr.filter((e) => {
        const elem = e as { [key: string]: any };
        // Handle nested keys like "user.id" by splitting on dots and traversing the object
        const keyParts = key.split(".");
        let value = elem;

        for (const part of keyParts) {
            if (value === null || value === undefined) return false;
            value = value[part];
        }

        const isDuplicate = uniqueIds.has(value);
        uniqueIds.add(value);

        if (!isDuplicate) return true;
        return false;
    });
};

export const downloadFileFromUrl = async (
    url: string,
    filename: string,
    body?: any,
) => {
    const response = await fetch(
        url,
        body
            ? {
                  method: "POST",
                  headers: {
                      "Content-Type": "application/json",
                  },
                  body: JSON.stringify(body),
              }
            : {},
    );

    if (!response.ok) {
        throw new Error("Download failed");
    }
    const blob = await response.blob();
    const clientUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = clientUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(clientUrl);
    document.body.removeChild(a);
};

export const processInChunksWith = async <T>(
    list: T[],
    fn: (chunk: T[]) => Promise<any>,
    chunkSize: number,
) => {
    const promises = [];
    for (let i = 0; i < list.length; i += chunkSize) {
        const chunk = list.slice(i, i + chunkSize);
        promises.push(fn(chunk));
    }
    return Promise.all(promises);
};

export const arraysAreEqual = <T>(arr1: T[], arr2: T[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }
    return true;
};

export type GeoLocation = {
    latitude: number;
    longitude: number;
    country?: string;
    region?: string;
};

export const getGeoLocation = (
    headers?: ReadonlyHeaders,
    req?: NextApiRequest,
): GeoLocation => {
    let latitude, longitude, country, region;
    if (headers) {
        latitude = headers.get("x-vercel-ip-latitude");
        longitude = headers.get("x-vercel-ip-longitude");
        country = headers.get("x-vercel-ip-country");
        region = headers.get("x-vercel-ip-country-region");
    } else if (req) {
        latitude = req.headers["x-vercel-ip-latitude"];
        longitude = req.headers["x-vercel-ip-longitude"];
        country = req.headers["x-vercel-ip-country"];
        region = req.headers["x-vercel-ip-country-region"];
    }

    // We need this since we don't get vercel geo information in dev / localhost
    // mode. This location is Austin, TX. Change these coords to test
    // different locations locally.
    const DEVELOPMENT_GEO_LOCATION: GeoLocation = {
        latitude: Number(env.LOCAL_LATITUDE),
        longitude: Number(env.LOCAL_LONGITUDE),
        country: "PL",
        region: "WAW",
    };

    if (process.env.NODE_ENV === "development") return DEVELOPMENT_GEO_LOCATION;
    return {
        latitude: parseFloat(latitude as string),
        longitude: parseFloat(longitude as string),
        country: country as string,
        region: region as string,
    };
};
